import React, { useEffect, useState, useCallback } from "react";

interface Params {
    elem: HTMLElement | null;
    scrollX: boolean;
    scrollY: boolean;
}

interface Result {
    handleMouseDown: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
    isScrolling: boolean;
}

export default function useDragToScroll({ elem, scrollX, scrollY }: Params): Result {
    const [isScrolling, setIsScrolling] = useState<boolean>(false);
    const [scrollPosition, setScrollPosition] = useState<{
        clientX: number;
        clientY: number;
        scrollLeft: number;
        scrollTop: number;
    }>({ clientX: 0, clientY: 0, scrollLeft: 0, scrollTop: 0 });

    const handleMouseDown = (event: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
        if (elem) {
            const { clientX, clientY } = event;
            const { scrollLeft, scrollTop } = elem;
            setScrollPosition({ scrollLeft, scrollTop, clientX, clientY });
            setIsScrolling(true);
            event.stopPropagation();
        }
    };

    const handleMouseMove = useCallback(
        (event: MouseEvent) => {
            const { clientX, scrollLeft, scrollTop, clientY } = scrollPosition;
            if (elem) {
                if (scrollX) {
                    elem.scrollLeft = scrollLeft + clientX - event.clientX;
                }
                if (scrollY) {
                    elem.scrollTop = scrollTop + clientY - event.clientY;
                }
            }
        },
        [scrollPosition, elem, scrollX, scrollY]
    );

    const handleMouseUp = useCallback(
        (event: MouseEvent): void => {
            setIsScrolling(false);
            event.stopPropagation();
        },
        [setIsScrolling]
    );

    useEffect(() => {
        if (isScrolling) {
            window.addEventListener("mousemove", handleMouseMove);
            window.addEventListener("mouseup", handleMouseUp);
        }
        return (): void => {
            window.removeEventListener("mouseup", handleMouseUp);
            window.removeEventListener("mousemove", handleMouseMove);
        };
    }, [isScrolling, handleMouseMove, handleMouseUp]);

    return { handleMouseDown, isScrolling };
}
